import {Card, Label, Tooltip} from 'platform/components';
import {Grid, Heading, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency, useFormatNumber} from 'platform/locale';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {getQuantityWithHandlingUnit} from '@dms/warehouse/shared';

import {EMPTY_PLACEHOLDER} from 'shared/web';

import {getAnnualPartUsage} from './getAnnualPartUsage';
import {getDaysOfSupply} from './getDaysOfSupply';
import {getInventoryTurnover} from './getInventoryTurnover';

interface StatisticsKpisProps {
  months: {netIssuedQuantity: number | null}[];
  averageInventoryQuantity: number | null;
  currentInventoryQuantity?: number;
  currentInventoryValue?: number;
  oldestStockAge?: number;
  oldestStockDate?: string;
  historicalBasis?: string;
  currency: string;
  handlingUnitLabel: string;
}

interface KpiTileProps {
  label: string;
  description: string;
  value: string | null | undefined;
  secondary?: string;
  basis?: string;
}

function KpiTile(props: KpiTileProps) {
  return (
    <Card>
      <VStack spacing={1}>
        <HStack spacing={1}>
          <Label>{props.label}</Label>
          <Tooltip description={props.description}>
            <Icon value="action/help" color="text.secondary" size={4} />
          </Tooltip>
        </HStack>
        <Heading size={3}>{props.value}</Heading>
        <Show when={isNotNilOrEmpty(props.secondary) ? props.secondary : null}>
          {(secondary) => <Text color="secondary">{secondary}</Text>}
        </Show>
        <Show when={isNotNilOrEmpty(props.basis) ? props.basis : null}>
          {(basis) => <Text color="secondary">{basis}</Text>}
        </Show>
      </VStack>
    </Card>
  );
}

export function StatisticsKpis(props: StatisticsKpisProps) {
  const formatNumber = useFormatNumber();
  const formatCurrency = useFormatCurrency();
  const annualPartUsage = getAnnualPartUsage(props.months);
  const daysOfSupply = getDaysOfSupply(annualPartUsage, props.currentInventoryQuantity);
  const inventoryTurnover = getInventoryTurnover(annualPartUsage, props.averageInventoryQuantity);
  const today = i18n.t('general.labels.today');

  return (
    <Grid columns={[1, 2, 3, 6]} spacing={2}>
      <KpiTile
        label={i18n.t('entity.warehouse.labels.totalValue')}
        description={i18n.t('entity.warehouse.reporting.totalValueTooltip')}
        basis={today}
        value={
          props.currentInventoryValue === undefined
            ? EMPTY_PLACEHOLDER
            : formatCurrency(props.currentInventoryValue, props.currency, 2)
        }
        secondary={
          props.currentInventoryQuantity === undefined
            ? EMPTY_PLACEHOLDER
            : getQuantityWithHandlingUnit(props.currentInventoryQuantity, props.handlingUnitLabel)
        }
      />
      <KpiTile
        label={i18n.t('entity.warehouse.reporting.oldestRemainingBatch')}
        description={i18n.t('entity.warehouse.reporting.stockAgeNote')}
        basis={today}
        value={
          props.oldestStockAge === undefined
            ? EMPTY_PLACEHOLDER
            : formatNumber(props.oldestStockAge, 0)
        }
        secondary={props.oldestStockDate ?? EMPTY_PLACEHOLDER}
      />
      <KpiTile
        label={i18n.t('entity.warehouse.reporting.annualPartUsage')}
        description={i18n.t('entity.warehouse.reporting.annualPartUsageTooltip')}
        basis={props.historicalBasis}
        value={
          annualPartUsage === null
            ? EMPTY_PLACEHOLDER
            : getQuantityWithHandlingUnit(annualPartUsage, props.handlingUnitLabel)
        }
      />
      <KpiTile
        label={i18n.t('entity.warehouse.reporting.averageInventoryQuantity')}
        description={i18n.t('entity.warehouse.reporting.averageInventoryQuantityTooltip')}
        basis={props.historicalBasis}
        value={
          props.averageInventoryQuantity === null
            ? EMPTY_PLACEHOLDER
            : getQuantityWithHandlingUnit(
                formatNumber(props.averageInventoryQuantity, 2) ?? EMPTY_PLACEHOLDER,
                props.handlingUnitLabel
              )
        }
      />
      <KpiTile
        label={i18n.t('entity.warehouse.reporting.daysOfSupply')}
        description={i18n.t('entity.warehouse.reporting.daysOfSupplyTooltip')}
        basis={
          isNotNilOrEmpty(props.historicalBasis) ? `${today} · ${props.historicalBasis}` : today
        }
        value={daysOfSupply === null ? EMPTY_PLACEHOLDER : formatNumber(daysOfSupply, 1)}
      />
      <KpiTile
        label={i18n.t('entity.warehouse.reporting.inventoryTurnover')}
        description={i18n.t('entity.warehouse.reporting.inventoryTurnoverTooltip')}
        basis={props.historicalBasis}
        value={inventoryTurnover === null ? EMPTY_PLACEHOLDER : formatNumber(inventoryTurnover, 2)}
      />
    </Grid>
  );
}
